import { ReactElement, useMemo } from 'react';
import { formatISO, startOfYear, endOfYear } from 'date-fns';
import { Visibility, VisibilityProps } from '../common';
import { usePeriod } from '../usePeriod';
import { columns } from './columns';
import { Row } from './types';
import { CompanyFilterChip } from 'modules/core/company/components';
import { TableColumns } from '@ff-it/ui';
import { ReportTable } from 'components/ReportTable';
import { StateFilterChip } from '../StateFilterChip';
import { TypeFilterChip } from '../TypeFilterChip';
import { MonthRangeFilterChip } from 'components/filters/MonthRangeFilterChip';
import { FilterSeparator } from 'components/filters/FilterSeparator';
import { BrandFilterChip } from 'components/filters/BrandFilterChip';
import { EnterpriseFilterChip } from 'components/filters/EnterpriseFilterChip';
import { SearchFilterInput } from 'components/filters/SearchFilterInput';

const resolveColumns = (visibility: Visibility): TableColumns<Row> => {
  switch (visibility) {
    case 'GLOBAL':
      return columns;
    case 'LOCAL':
      // no agency
      return columns.slice(1);
    case 'LOCAL_MANAGED':
    case 'LOCAL_TYPE':
      // no agency, no bonuses
      return columns.slice(1, 10);
  }
};

export function Campaign({ visibility, ...props }: VisibilityProps): ReactElement {
  const { date_from: minDate, date_to: maxDate } = usePeriod();
  const initialFilter = {
    date_from: formatISO(startOfYear(new Date()), { representation: 'date' }),
    date_to: formatISO(endOfYear(new Date()), { representation: 'date' }),
  };

  const url = `reporting/campaign/${visibility.toLowerCase()}${'type' in props ? `/${props.type}/` : '/'}`;

  const columns = useMemo(() => {
    return resolveColumns(visibility);
  }, [visibility]);

  return (
    <ReportTable
      url={url}
      defaultFilter={initialFilter}
      columns={columns}
      filterFields={
        <>
          <SearchFilterInput name="search_campaign" placeholder="Campaign title or number" />
          <MonthRangeFilterChip
            label="Period"
            fromName="date_from"
            toName="date_to"
            minDate={minDate}
            maxDate={maxDate}
            defaultFrom={initialFilter.date_from}
            defaultTo={initialFilter.date_to}
          />
          <CompanyFilterChip name="clients" label="Client" isMulti />
          <CompanyFilterChip name="payers" label="Payer" isMulti />
          <BrandFilterChip name="brand" />
          <EnterpriseFilterChip name="agency" isMulti defaultValueLabel="All agencies" />
          <FilterSeparator />
          <StateFilterChip />
          {visibility !== 'LOCAL_TYPE' && <TypeFilterChip global={visibility === 'GLOBAL'} />}
        </>
      }
    />
  );
}
